import type { KbAnswerResponse } from "../kb/types";
import { inferTopicKey } from "../contacts/contactsTopics";

export function contactHasTopic(c: any, topic: string): boolean {
  const needle = String(topic || "").trim().toLowerCase();
  if (!needle) return false;
  return Array.isArray(c?.topics) && c.topics.some((t: unknown) => String(t || "").trim().toLowerCase() === needle);
}

export function matchesPreferredTag(c: any, preferredTags: string[]): boolean {
  return preferredTags.some((tag) => contactHasTopic(c, tag));
}

function expandRequiredTagsWithAliases(requiredTags: string[]): string[] {
  const aliasMap: Record<string, string[]> = {
    primary_advertising_contact: [
      "marketing",
      "marketing_materials",
      "creative_assets",
      "facebook_ads",
      "google_ads",
      "website_updates",
      "signage_requests",
    ],
    primary_crm_contact: ["crm", "crm_access", "crm_workflows"],
    primary_paperwork_contact: ["paperwork", "dealer_paperwork", "deal_packet", "order_entry", "funding_docs", "commission_questions"],
    primary_delivery_contact: ["delivery", "delivery_scheduling", "delivery_reschedule", "site_access_requirements"],
    primary_repair_contact: ["repair", "repair_requests", "warranty_claims", "service_scheduling"],
    primary_tech_contact: ["tech", "device_setup"],
  };
  const out = [...requiredTags];
  for (const t of requiredTags) {
    out.push(...(aliasMap[t] || []));
  }
  return uniqueTags(out);
}

export function contactMatchesFallbackIdentity(
  c: any,
  fallback?: { source: "user" | "external" | null; sourceId: string | null } | null
): boolean {
  return Boolean(
    fallback?.source && fallback?.sourceId && c?.source === fallback.source && String(c?.sourceId || "") === fallback.sourceId
  );
}

export function narrowToGeneralSupportFallback(
  rows: any[],
  preferredTags: string[],
  fallback?: { source: "user" | "external" | null; sourceId: string | null } | null
): any[] {
  if (!rows.length) return rows;
  const fallbackRow =
    rows.find((row) => contactMatchesFallbackIdentity(row, fallback)) ||
    rows.find((row) => contactHasTopic(row, "general_support")) ||
    null;
  const explicitMatches = preferredTags.length ? rows.filter((row) => matchesPreferredTag(row, preferredTags)) : [];
  const narrowed = [...(fallbackRow ? [fallbackRow] : []), ...explicitMatches];
  const seen = new Set<string>();
  return narrowed.filter((row) => {
    const key = `${row?.source || ""}:${row?.sourceId || row?.name || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function inferPreferredPrimaryTags(q: string): string[] {
  const s = (q || "").toLowerCase();
  const tk = inferTopicKey(q);
  if (tk) {
    const topicMap: Record<string, string> = {
      creative_assets: "primary_advertising_contact",
      facebook_ads: "primary_advertising_contact",
      google_ads: "primary_advertising_contact",
      website_updates: "primary_advertising_contact",
      marketing_materials: "primary_advertising_contact",
      signage_requests: "primary_advertising_contact",
      crm_access: "primary_crm_contact",
      crm_workflows: "primary_crm_contact",
      dealer_paperwork: "primary_paperwork_contact",
      deal_packet: "primary_paperwork_contact",
      order_entry: "primary_paperwork_contact",
      cash_contracts: "primary_paperwork_contact",
      funding_docs: "primary_paperwork_contact",
      delivery_scheduling: "primary_delivery_contact",
      delivery_reschedule: "primary_delivery_contact",
      site_access_requirements: "primary_delivery_contact",
      repair_requests: "primary_repair_contact",
      warranty_claims: "primary_repair_contact",
      service_scheduling: "primary_repair_contact",
      device_setup: "primary_tech_contact",
    };
    const tag = topicMap[tk];
    if (tag) return [tag];
  }

  if (/(advertis|marketing|website|opengraph|seo|facebook|meta|google\s+ads?|ppc|creative|graphic|signage|\bads?\b)/i.test(s)) {
    return ["primary_advertising_contact"];
  }
  if (/(crm|ghl|go\s*high\s*level|pipeline|workflow|automation|trigger|permissions?|login)/i.test(s)) {
    return ["primary_crm_contact"];
  }
  if (/(paperwork|deal|deal\s+packet|packet|contract|funding|docs?|order\s+entry|submit\s+a\s+deal)/i.test(s)) {
    return ["primary_paperwork_contact"];
  }
  if (/(commission|compensat|payroll|bonus|draw\b)/i.test(s)) {
    return ["primary_paperwork_contact"];
  }
  if (/(deliver|resched|schedule\s+delivery|site\s+access|gate\s+code|clearance)/i.test(s)) {
    return ["primary_delivery_contact"];
  }
  if (/(repair|warranty|claim|service\s+request)/i.test(s)) {
    return ["primary_repair_contact"];
  }
  if (/(tablet|device|computer|phone|app|software|setup|install|login\s+issue)/i.test(s)) {
    return ["primary_tech_contact"];
  }

  return [];
}

function uniqueTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const t = String(raw || "").trim().toLowerCase();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

const KB_HINT_TO_PRIMARY_TAG: Array<{ re: RegExp; tag: string }> = [
  { re: /(commission|compensat|bonus|payroll|spiff)/i, tag: "primary_paperwork_contact" },
  { re: /(paperwork|deal packet|order entry|contract|funding docs?)/i, tag: "primary_paperwork_contact" },
  { re: /(crm|ghl|go high level|workflow|pipeline|automation|trigger|permissions?)/i, tag: "primary_crm_contact" },
  { re: /(delivery|resched|site access|gate code|clearance)/i, tag: "primary_delivery_contact" },
  { re: /(warranty|repair|service request|claim)/i, tag: "primary_repair_contact" },
  { re: /(advertis|marketing|creative|signage|facebook|meta|google ads?|website|\bads?\b)/i, tag: "primary_advertising_contact" },
  { re: /(device|tablet|setup|install|login issue)/i, tag: "primary_tech_contact" },
];

export function expandPreferredPrimaryTagsFromKb(q: string, sources: Array<{ title?: string | null; slug?: string | null }>): string[] {
  const haystack = [q, ...sources.slice(0, 4).map((s) => `${s.title || ""} ${s.slug || ""}`)].join(" ");
  const tags: string[] = [];
  for (const rule of KB_HINT_TO_PRIMARY_TAG) {
    if (rule.re.test(haystack)) tags.push(rule.tag);
  }
  return uniqueTags(tags);
}

export function strictContactSelection(args: {
  rows: any[];
  requiredTags: string[];
  limit: number;
  fallback?: { source: "user" | "external" | null; sourceId: string | null } | null;
}): {
  selected: any[];
  exactMatchCount: number;
  relatedTagSuggestions: string[];
} {
  const { rows, requiredTags, limit, fallback } = args;
  const normalizedTags = uniqueTags(requiredTags);
  const matchTags = expandRequiredTagsWithAliases(normalizedTags);
  const fallbackRow =
    rows.find((row) => contactMatchesFallbackIdentity(row, fallback)) ||
    rows.find((row) => contactHasTopic(row, "general_support")) ||
    null;

  const exactPrimary = matchTags.length
    ? rows.filter((row) => matchesPreferredTag(row, matchTags))
    : [];

  const seen = new Set<string>();
  const selected = [...(fallbackRow ? [fallbackRow] : []), ...exactPrimary].filter((row) => {
    const key = `${row?.source || ""}:${row?.sourceId || row?.name || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, Math.max(1, limit));

  const relatedTagSuggestions = exactPrimary.length > 0 ? [] : suggestRelatedPrimaryTags(normalizedTags);
  return { selected, exactMatchCount: exactPrimary.length, relatedTagSuggestions };
}

export function suggestRelatedPrimaryTags(requiredTags: string[]): string[] {
  const relatedMap: Record<string, string[]> = {
    primary_paperwork_contact: ["primary_crm_contact", "primary_dealer_contact"],
    primary_crm_contact: ["primary_paperwork_contact", "primary_tech_contact"],
    primary_delivery_contact: ["primary_repair_contact", "primary_dealer_contact"],
    primary_repair_contact: ["primary_delivery_contact", "primary_dealer_contact"],
    primary_advertising_contact: ["primary_crm_contact", "primary_dealer_contact"],
    primary_tech_contact: ["primary_crm_contact", "primary_dealer_contact"],
    primary_dealer_contact: ["primary_paperwork_contact", "primary_crm_contact"],
  };
  const out: string[] = [];
  for (const t of requiredTags) {
    out.push(...(relatedMap[t] || []));
  }
  return uniqueTags(out).slice(0, 3);
}

export function kbFetchFailedResponse(err: unknown, lane: "contacts" | "answers" | "pricing" = "answers"): KbAnswerResponse {
  const base =
    lane === "contacts"
      ? "We couldn’t complete this contacts lookup right now. Retry in a minute, or ask again with a specific person/team."
      : lane === "pricing"
        ? "We couldn’t complete this pricing lookup right now. Retry in a minute, or ask again with product/size details."
        : "We couldn’t complete this knowledge lookup right now. Retry in a minute.";
  let detail = "";
  const dev = typeof import.meta !== "undefined" && Boolean((import.meta as ImportMeta).env?.DEV);
  const debugKb =
    String((import.meta as ImportMeta).env?.VITE_DEBUG_KB || "").toLowerCase() === "true" ||
    String((import.meta as ImportMeta).env?.VITE_DEBUG_KB || "") === "1";
  if ((dev || debugKb) && err instanceof Error && err.message) {
    detail = `\n\n[Details] ${err.message.slice(0, 600)}`;
  }
  return {
    mode: "no_answer",
    answer: base + detail,
    bestConfidence: 0,
    confidenceBand: "low",
    sources: [],
  };
}
